import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import HelpTooltip from "../components/shared/HelpTooltip";
import TourRestartButton from "../components/shared/TourRestartButton";
import { useProductTour } from "../hooks/useProductTour";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ArrowRight, Bell, Clock, Edit3, Gift, ListChecks, MailPlus, Plus, Tag, Trash2, UserCog, Zap } from "lucide-react";
import { toast } from "sonner";

const blankForm = {
  name: "",
  enabled: true,
  trigger_type: "lead.created",
  trigger_column_id: "",
  trigger_pipeline_id: "",
  trigger_minutes: 5,
  delay_minutes: 0,
  action_type: "send_message",
  action_template_id: "",
  action_message_text: "Olá {{nome}}, tudo bem? Recebemos seu contato e já vamos te atender.",
  action_agent_id: "",
  action_tag: "",
  action_pipeline_id: "",
  action_column_id: "",
  action_task_type: "follow-up",
  action_task_title: "Acompanhar lead",
  action_task_description: "",
  action_task_due: "1h",
  action_task_due_days: 1,
  action_task_assignee: "lead_owner",
  action_priority: "high",
  action_notify_user_id: "",
};

const triggerOptions = [
  ["lead.created", "Novo lead entra"],
  ["lead.moved", "Lead movido para etapa"],
  ["lead.pipeline_moved", "Lead movido para funil"],
  ["lead.unassigned_too_long", "Lead sem responsável por X minutos"],
  ["lead.stale_in_stage", "Lead parado na etapa por X tempo"],
  ["message.received", "Nova mensagem recebida"],
  ["task.overdue", "Tarefa vencida"],
  ["repurchase_reminder.due", "Lembrete de recompra/presente vence"],
];

const actionOptions = [
  ["send_message", "Enviar mensagem"],
  ["assign_agent", "Atribuir responsável"],
  ["move_pipeline", "Mover para funil"],
  ["move_stage", "Mover para etapa"],
  ["create_task", "Criar tarefa"],
  ["add_tag", "Adicionar tag"],
  ["mark_priority", "Marcar prioridade"],
  ["notify_user", "Notificar responsável"],
  ["repurchase_reminder", "Preparar lembrete de recompra/presente"],
];

const presets = [
  {
    title: "Boas-vindas automática",
    icon: MailPlus,
    form: {
      name: "Boas-vindas automática",
      trigger_type: "lead.created",
      action_type: "send_message",
      action_message_text: "Olá {{nome}}, tudo bem? Recebemos seu contato e já vamos te atender.",
    },
  },
  {
    title: "Distribuir lead sem responsável",
    icon: UserCog,
    form: {
      name: "Distribuir lead sem responsável",
      trigger_type: "lead.unassigned_too_long",
      trigger_minutes: 5,
      action_type: "assign_agent",
    },
  },
  {
    title: "Mover lead após 5 minutos",
    icon: Clock,
    form: {
      name: "Mover lead após 5 minutos",
      trigger_type: "lead.created",
      delay_minutes: 5,
      action_type: "move_stage",
    },
  },
  {
    title: "Atribuir responsável por etapa",
    icon: UserCog,
    form: {
      name: "Atribuir responsável por etapa",
      trigger_type: "lead.moved",
      action_type: "assign_agent",
    },
  },
  {
    title: "Criar tarefa de follow-up",
    icon: ListChecks,
    form: {
      name: "Criar tarefa de follow-up",
      trigger_type: "lead.created",
      delay_minutes: 60,
      action_type: "create_task",
      action_task_title: "Acompanhar lead",
      action_task_due: "1h",
    },
  },
  {
    title: "Alerta de lead parado",
    icon: Bell,
    form: {
      name: "Alerta de lead parado",
      trigger_type: "lead.stale_in_stage",
      trigger_minutes: 120,
      action_type: "notify_user",
      action_task_title: "Lead parado na etapa",
    },
  },
  {
    title: "Reengajamento",
    icon: Zap,
    form: {
      name: "Reengajamento",
      trigger_type: "message.received",
      action_type: "create_task",
      action_task_title: "Responder nova mensagem",
      action_task_due: "30m",
    },
  },
  {
    title: "Lembrete de Recompra/Presente",
    icon: Gift,
    form: {
      name: "Lembrete de Recompra/Presente",
      trigger_type: "repurchase_reminder.due",
      action_type: "repurchase_reminder",
    },
  },
];

const FIXED_PLACEHOLDERS = ["nome", "telefone", "empresa", "responsavel", "funil", "etapa"];

const dueOptions = [
  ["now", "imediatamente"],
  ["5m", "em 5 minutos"],
  ["30m", "em 30 minutos"],
  ["1h", "em 1 hora"],
  ["tomorrow", "amanhã"],
  ["xdays", "em X dias"],
];

function SelectField({ value, onChange, children, testid, disabled }) {
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-sm text-slate-100 outline-none focus:border-[#25D366] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </select>
  );
}

function Option({ value, children }) {
  return <option value={value}>{children}</option>;
}

function labelFor(options, value) {
  return options.find(([id]) => id === value)?.[1] || value || "";
}

// Passos do tour guiado -- constante no módulo (não muda entre renders, não
// precisa de useMemo) já que todos os elementos-alvo existem sempre nesta
// tela, independente de o usuário já ter automações criadas ou não.
const AUTOMATIONS_TOUR_STEPS = [
  {
    element: '[data-testid="automations-content"]',
    popover: {
      title: "Automações",
      description: "Fazem tarefas repetitivas sozinhas: mover lead de etapa, atribuir responsável, mandar mensagem, criar tarefa... sem você precisar fazer isso na mão toda vez.",
    },
  },
  {
    element: '[data-testid="automations-presets-section"]',
    popover: {
      title: "Atalhos prontos",
      description: "O jeito mais rápido de começar: escolha um atalho já configurado (ex: lembrete de recompra, distribuir leads novos) e só ajuste os detalhes.",
    },
  },
  {
    element: '[data-testid="automations-list-section"]',
    popover: {
      title: "Suas automações",
      description: "Tudo que você criar aparece aqui, com um interruptor pra ativar/desativar sem precisar excluir e recriar depois.",
    },
  },
  {
    element: '[data-testid="new-automation-btn"]',
    popover: {
      title: "Criar do zero",
      description: "Quer algo bem específico pro seu negócio? Monte uma automação personalizada aqui: escolha o gatilho (\"Se isso acontecer\") e a ação (\"então faça aquilo\").",
    },
  },
];

function fmtLastRun(value) {
  if (!value) return "Nunca executou";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return "Executou recentemente";
  }
}

export default function Automations() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [team, setTeam] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);

  const { start: startAutomationsTour } = useProductTour("automations", AUTOMATIONS_TOUR_STEPS);

  const reload = async () => {
    const [a, t, c, tm, p, cf] = await Promise.all([
      api.get("/automations"),
      api.get("/templates"),
      api.get("/columns"),
      api.get("/team"),
      api.get("/pipelines"),
      api.get("/custom-field-definitions").catch(() => ({ data: [] })),
    ]);
    setItems(Array.isArray(a.data) ? a.data : []);
    setTemplates(Array.isArray(t.data) ? t.data : []);
    setColumns(Array.isArray(c.data) ? c.data : []);
    setTeam(Array.isArray(tm.data) ? tm.data : []);
    setPipelines(Array.isArray(p.data) ? p.data : []);
    setCustomFields(Array.isArray(cf.data) ? cf.data : []);
  };

  useEffect(() => { reload(); }, []);

  const openPreset = (preset) => {
    setEditing(null);
    setDraft({ ...blankForm, ...preset.form });
    setOpen(true);
  };

  const toggle = async (automation) => {
    await api.patch(`/automations/${automation.id}`, { enabled: !automation.enabled });
    reload();
  };

  const remove = async (automation) => {
    if (!window.confirm(`Excluir automação "${automation.name}"?`)) return;
    await api.delete(`/automations/${automation.id}`);
    toast.info("Automação excluída");
    reload();
  };

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar
          title="Automações"
          subtitle="Crie rotinas para atender, distribuir e acompanhar leads sem esforço manual"
          actions={(
            <Button
              data-testid="new-automation-btn"
              onClick={() => { setEditing(null); setDraft(null); setOpen(true); }}
              className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
            >
              <Plus className="w-4 h-4" /> Nova automação
            </Button>
          )}
        />

        <div data-testid="automations-content" className="px-6 py-6 max-w-6xl space-y-6">
          <section data-testid="automations-presets-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>Atalhos prontos</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {presets.map((preset) => {
                const Icon = preset.icon;
                return (
                  <button
                    key={preset.title}
                    onClick={() => openPreset(preset)}
                    className="text-left bg-slate-900/70 border border-slate-800 hover:border-[#25D366]/60 rounded-lg p-4 transition"
                  >
                    <Icon className="w-5 h-5 text-[#25D366] mb-3" />
                    <div className="text-sm font-semibold text-slate-100">{preset.title}</div>
                  </button>
                );
              })}
            </div>
          </section>

          <section data-testid="automations-list-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>Automações criadas</h2>
              <span className="text-xs text-slate-500">{items.length} no total</span>
            </div>

            {items.length === 0 ? (
              <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-lg py-16 text-center">
                <Zap className="w-8 h-8 text-[#25D366] mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-100">Nenhuma automação ainda</h3>
                <p className="text-sm text-slate-400 mt-1">Use um atalho pronto ou crie uma regra personalizada.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((automation) => (
                  <AutomationRow
                    key={automation.id}
                    automation={automation}
                    columns={columns}
                    pipelines={pipelines}
                    team={team}
                    templates={templates}
                    onToggle={toggle}
                    onEdit={() => { setEditing(automation); setDraft(null); setOpen(true); }}
                    onDelete={() => remove(automation)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      <AutomationDialog
        open={open}
        editing={editing}
        draft={draft}
        templates={templates}
        columns={columns}
        pipelines={pipelines}
        team={team}
        customFields={customFields}
        onClose={() => setOpen(false)}
        onSaved={() => { setOpen(false); reload(); }}
      />

      <TourRestartButton onClick={startAutomationsTour} label="Rever tour das Automações" />
    </div>
  );
}

function AutomationRow({ automation, columns, pipelines, team, templates, onToggle, onEdit, onDelete }) {
  const column = columns.find((item) => item.id === automation.trigger_column_id || item.id === automation.action_column_id);
  const pipeline = pipelines.find((item) => item.id === automation.trigger_pipeline_id || item.id === automation.action_pipeline_id);
  const agent = team.find((item) => item.id === automation.action_agent_id || item.id === automation.action_notify_user_id);
  const template = templates.find((item) => item.id === automation.action_template_id);
  const details = [column?.name, pipeline?.name, agent?.name, template?.title, automation.action_tag].filter(Boolean).join(" · ");

  return (
    <div data-testid={`automation-row-${automation.id}`} className={`bg-slate-900/70 border border-slate-800 rounded-lg p-4 ${automation.enabled ? "" : "opacity-60"}`}>
      <div className="flex items-start gap-4">
        <button
          data-testid={`automation-toggle-${automation.id}`}
          onClick={() => onToggle(automation)}
          className={`w-11 h-11 rounded-lg flex items-center justify-center ${automation.enabled ? "bg-[#25D366]/15 text-[#25D366]" : "bg-slate-800 text-slate-500"}`}
          title={automation.enabled ? "Ativa" : "Inativa"}
        >
          <Zap className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold text-slate-100">{automation.name}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${automation.enabled ? "bg-[#25D366]/15 text-[#25D366]" : "bg-slate-800 text-slate-400"}`}>
              {automation.enabled ? "Ativa" : "Inativa"}
            </span>
            <span className="text-[11px] text-slate-500">{automation.runs || 0} execuções</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            <span className="px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-blue-300">
              Quando: {labelFor(triggerOptions, automation.trigger_type)}
            </span>
            {automation.delay_minutes > 0 && (
              <span className="px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-amber-300">
                Aguardar: {automation.delay_minutes} min
              </span>
            )}
            <ArrowRight className="w-3 h-3 text-slate-600" />
            <span className="px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-[#25D366]">
              Então: {labelFor(actionOptions, automation.action_type)}
            </span>
          </div>
          {details && <div className="text-xs text-slate-400 mt-2">{details}</div>}
          <div className="text-[11px] text-slate-500 mt-2">Última execução: {fmtLastRun(automation.last_run)}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="p-2 text-slate-400 hover:text-[#25D366] hover:bg-slate-800 rounded-md" title="Editar">
            <Edit3 className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md" title="Excluir">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AutomationDialog({ open, editing, draft, templates, columns, pipelines, team, customFields, onClose, onSaved }) {
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ ...blankForm, ...editing });
    } else {
      setForm({ ...blankForm, ...(draft || {}) });
    }
  }, [editing, draft, open]);

  const selectedTemplate = templates.find((item) => item.id === form.action_template_id);
  // Antes caía num fallback pra "todas as colunas" quando nenhum funil
  // estava selecionado -- misturava Ganho/Perdido/Novo Lead de todos os
  // funis juntos, sem indicar de qual funil cada um era. Agora fica vazio
  // até o funil ser escolhido (o select correspondente também fica
  // desabilitado, ver abaixo).
  const columnsForActionPipeline = form.action_pipeline_id ? columns.filter((item) => item.pipeline_id === form.action_pipeline_id) : [];

  const preview = useMemo(() => {
    const text = selectedTemplate?.content || form.action_message_text || "";
    return text
      .replaceAll("{{nome}}", "Maria")
      .replaceAll("{{telefone}}", "5511999999999")
      .replaceAll("{{empresa}}", "Empresa Exemplo")
      .replaceAll("{{responsavel}}", "Ana")
      .replaceAll("{{funil}}", "Vendas")
      .replaceAll("{{etapa}}", "Novo Lead");
  }, [form.action_message_text, selectedTemplate]);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const isRepurchaseReminder = form.action_type === "repurchase_reminder";

  const missingReminderDestination = isRepurchaseReminder && (!form.action_pipeline_id || !form.action_column_id);

  const save = async () => {
    if (!form.name.trim()) return;
    if (missingReminderDestination) {
      toast.error("Selecione o funil e a etapa de destino do lembrete de recompra/presente");
      return;
    }
    setBusy(true);
    // "Lembrete de Recompra/Presente" não usa gatilho genérico nem delay: o
    // disparo é sempre guiado pelo due_at de cada lembrete salvo no lead
    // (ver queue_repurchase_reminder no backend). Forçamos aqui para não
    // depender só da UI esconder os campos corretamente.
    const effectiveTriggerType = isRepurchaseReminder ? "repurchase_reminder.due" : form.trigger_type;
    const payload = {
      ...form,
      name: form.name.trim(),
      trigger_type: effectiveTriggerType,
      delay_minutes: isRepurchaseReminder ? 0 : (Number(form.delay_minutes) || 0),
      trigger_minutes: Number(form.trigger_minutes) || 5,
      action_task_due_days: Number(form.action_task_due_days) || 1,
      trigger_column_id: effectiveTriggerType === "lead.moved" ? form.trigger_column_id || null : null,
      trigger_pipeline_id: effectiveTriggerType === "lead.pipeline_moved" ? form.trigger_pipeline_id || null : null,
      action_template_id: ["send_message", "send_template", "repurchase_reminder"].includes(form.action_type) ? form.action_template_id || null : null,
      action_message_text: ["send_message", "send_template"].includes(form.action_type) ? form.action_message_text || null : null,
      action_agent_id: ["assign_agent", "create_task"].includes(form.action_type) ? form.action_agent_id || null : null,
      action_pipeline_id: ["move_pipeline", "repurchase_reminder"].includes(form.action_type) ? form.action_pipeline_id || null : null,
      action_column_id: ["move_stage", "move_pipeline", "repurchase_reminder"].includes(form.action_type) ? form.action_column_id || null : null,
      action_tag: form.action_type === "add_tag" ? form.action_tag || null : null,
      action_notify_user_id: form.action_type === "notify_user" ? form.action_notify_user_id || null : null,
      action_priority: form.action_type === "mark_priority" ? form.action_priority : null,
    };
    try {
      if (editing) await api.patch(`/automations/${editing.id}`, payload);
      else await api.post("/automations", payload);
      toast.success(editing ? "Automação atualizada" : "Automação criada");
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao salvar automação");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="automation-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{editing ? "Editar automação" : "Nova automação"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-400">Nome da automação</Label>
            <Input data-testid="auto-name" value={form.name} onChange={(event) => set({ name: event.target.value })} className="bg-slate-800 border-slate-700 mt-1" />
          </div>

          {!isRepurchaseReminder && (
            <Panel title="Quando isso acontecer">
              <SelectField testid="auto-trigger" value={form.trigger_type} onChange={(value) => set({ trigger_type: value })}>
                {triggerOptions.filter(([value]) => value !== "repurchase_reminder.due").map(([value, label]) => <Option key={value} value={value}>{label}</Option>)}
              </SelectField>
              {form.trigger_type === "lead.moved" && (
                <SelectField testid="auto-trigger-column" value={form.trigger_column_id} onChange={(value) => set({ trigger_column_id: value })}>
                  <Option value="">Escolha a etapa (qualquer funil)</Option>
                  {pipelines.map((pipeline) => {
                    const pipelineColumns = columns.filter((column) => column.pipeline_id === pipeline.id);
                    if (!pipelineColumns.length) return null;
                    return (
                      <optgroup key={pipeline.id} label={pipeline.name}>
                        {pipelineColumns.map((column) => <Option key={column.id} value={column.id}>{column.name}</Option>)}
                      </optgroup>
                    );
                  })}
                </SelectField>
              )}
              {form.trigger_type === "lead.pipeline_moved" && (
                <SelectField value={form.trigger_pipeline_id} onChange={(value) => set({ trigger_pipeline_id: value })}>
                  <Option value="">Escolha o funil</Option>
                  {pipelines.map((pipeline) => <Option key={pipeline.id} value={pipeline.id}>{pipeline.name}</Option>)}
                </SelectField>
              )}
              {["lead.unassigned_too_long", "lead.stale_in_stage"].includes(form.trigger_type) && (
                <NumberField
                  label="Tempo de espera em minutos"
                  value={form.trigger_minutes}
                  onChange={(value) => set({ trigger_minutes: value })}
                  helpText={
                    form.trigger_type === "lead.unassigned_too_long"
                      ? "\"Não atribuído\" observa só a ausência de responsável: ao criar o lead, o CRM agenda uma checagem para daqui a X minutos -- se nesse momento o lead continuar sem responsável, a automação dispara (uma única vez, só na criação)."
                      : "\"Parado na etapa\" observa o tempo sem mudar de etapa: toda vez que o lead entra numa etapa (na criação ou ao ser movido), o CRM agenda uma checagem para daqui a X minutos -- se o lead ainda estiver na mesma etapa (e com o mesmo responsável) nesse momento, a automação dispara."
                  }
                />
              )}
            </Panel>
          )}

          {!isRepurchaseReminder && (
            <Panel
              title="Aguardar"
              helpText="Atraso entre o gatilho disparar e a ação realmente rodar -- conceito diferente do 'Tempo de espera' dos gatilhos de tempo acima. Ex: se o gatilho é 'Lead entra numa etapa' e o delay é 10 minutos, a ação (enviar mensagem, criar tarefa etc.) só executa 10 minutos depois do lead entrar na etapa. Deixe 0 pra executar a ação imediatamente."
            >
              <NumberField label="Delay opcional em minutos" value={form.delay_minutes} onChange={(value) => set({ delay_minutes: value })} />
            </Panel>
          )}

          <Panel title="Então fazer">
            <SelectField
              testid="auto-action"
              value={form.action_type}
              onChange={(value) => set({
                action_type: value,
                ...(value === "repurchase_reminder" ? { trigger_type: "repurchase_reminder.due", delay_minutes: 0 } : {}),
              })}
            >
              {actionOptions.map(([value, label]) => <Option key={value} value={value}>{label}</Option>)}
            </SelectField>

            {["send_message", "send_template"].includes(form.action_type) && (
              <>
                <SelectField testid="auto-template" value={form.action_template_id} onChange={(value) => set({ action_template_id: value })}>
                  <Option value="">Texto livre</Option>
                  {templates.map((template) => <Option key={template.id} value={template.id}>{template.title}</Option>)}
                </SelectField>
                {!form.action_template_id && (
                  <Textarea value={form.action_message_text} onChange={(event) => set({ action_message_text: event.target.value })} className="bg-slate-800 border-slate-700 min-h-28" />
                )}
                <div className="bg-slate-950/60 border border-slate-800 rounded-md p-3 text-xs text-slate-300">
                  <div className="font-semibold text-slate-400 mb-1">Preview da mensagem</div>
                  {preview || "Escreva uma mensagem para visualizar."}
                </div>
              </>
            )}

            {["assign_agent", "create_task"].includes(form.action_type) && (
              <SelectField testid="auto-agent" value={form.action_agent_id} onChange={(value) => set({ action_agent_id: value })}>
                <Option value="">Escolha o responsável</Option>
                {team.map((member) => <Option key={member.id} value={member.id}>{member.name}</Option>)}
              </SelectField>
            )}

            {form.action_type === "move_pipeline" && (
              <div>
                <Label className="flex items-center gap-1.5 text-xs text-slate-400">
                  Mover lead para
                  <HelpTooltip text="Escolha primeiro o funil de destino -- a etapa só fica disponível depois disso, já filtrada pras etapas daquele funil. Deixar a etapa em branco usa a primeira etapa válida do funil escolhido." />
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                  <SelectField value={form.action_pipeline_id} onChange={(value) => set({ action_pipeline_id: value, action_column_id: "" })}>
                    <Option value="">Escolha o funil destino</Option>
                    {pipelines.map((pipeline) => <Option key={pipeline.id} value={pipeline.id}>{pipeline.name}</Option>)}
                  </SelectField>
                  <SelectField value={form.action_column_id} onChange={(value) => set({ action_column_id: value })} disabled={!form.action_pipeline_id}>
                    <Option value="">{form.action_pipeline_id ? "Usar primeira etapa válida" : "Escolha o funil primeiro"}</Option>
                    {columnsForActionPipeline.map((column) => <Option key={column.id} value={column.id}>{column.name}</Option>)}
                  </SelectField>
                </div>
              </div>
            )}

            {form.action_type === "move_stage" && (
              <SelectField value={form.action_column_id} onChange={(value) => set({ action_column_id: value })}>
                <Option value="">Escolha a etapa</Option>
                {pipelines.map((pipeline) => {
                  const pipelineColumns = columns.filter((column) => column.pipeline_id === pipeline.id);
                  if (!pipelineColumns.length) return null;
                  return (
                    <optgroup key={pipeline.id} label={pipeline.name}>
                      {pipelineColumns.map((column) => <Option key={column.id} value={column.id}>{column.name}</Option>)}
                    </optgroup>
                  );
                })}
              </SelectField>
            )}

            {form.action_type === "create_task" && (
              <>
                <Input value={form.action_task_type} onChange={(event) => set({ action_task_type: event.target.value })} placeholder="Tipo da tarefa" className="bg-slate-800 border-slate-700" />
                <Input value={form.action_task_title} onChange={(event) => set({ action_task_title: event.target.value })} placeholder="Título da tarefa" className="bg-slate-800 border-slate-700" />
                <Textarea value={form.action_task_description} onChange={(event) => set({ action_task_description: event.target.value })} placeholder="Descrição" className="bg-slate-800 border-slate-700" />
                <SelectField value={form.action_task_due} onChange={(value) => set({ action_task_due: value })}>
                  {dueOptions.map(([value, label]) => <Option key={value} value={value}>{label}</Option>)}
                </SelectField>
                {form.action_task_due === "xdays" && (
                  <NumberField label="Quantidade de dias" value={form.action_task_due_days} onChange={(value) => set({ action_task_due_days: value })} />
                )}
                <SelectField value={form.action_task_assignee} onChange={(value) => set({ action_task_assignee: value })}>
                  <Option value="lead_owner">Responsável do lead</Option>
                  <Option value="specific">Usuário específico acima</Option>
                </SelectField>
              </>
            )}

            {form.action_type === "add_tag" && (
              <Input data-testid="auto-tag" value={form.action_tag} onChange={(event) => set({ action_tag: event.target.value })} placeholder="Ex: quente, vip, reengajar" className="bg-slate-800 border-slate-700" />
            )}

            {form.action_type === "mark_priority" && (
              <SelectField value={form.action_priority} onChange={(value) => set({ action_priority: value })}>
                <Option value="high">Importante</Option>
                <Option value="urgent">Prioridade máxima</Option>
              </SelectField>
            )}

            {form.action_type === "notify_user" && (
              <>
                <SelectField value={form.action_notify_user_id} onChange={(value) => set({ action_notify_user_id: value })}>
                  <Option value="">Responsável do lead</Option>
                  {team.map((member) => <Option key={member.id} value={member.id}>{member.name}</Option>)}
                </SelectField>
                <Input value={form.action_task_title} onChange={(event) => set({ action_task_title: event.target.value })} placeholder="Título da notificação" className="bg-slate-800 border-slate-700" />
              </>
            )}

            {form.action_type === "repurchase_reminder" && (
              <>
                <div className="text-xs text-slate-400">
                  Este atalho é um interruptor: quando Ativo, todo lembrete futuro salvo
                  no card do lead (aba "Lembrete futuro") é processado automaticamente na
                  própria data escolhida pelo lojista — cria uma tarefa, notifica o
                  responsável, prepara uma mensagem de WhatsApp para revisão e move o lead
                  para o funil/etapa escolhidos abaixo. Não depende de nenhum tempo de espera.
                </div>
                <SelectField testid="auto-reminder-template" value={form.action_template_id} onChange={(value) => set({ action_template_id: value })}>
                  <Option value="">Usar mensagem padrão</Option>
                  {templates.map((template) => <Option key={template.id} value={template.id}>{template.title}</Option>)}
                </SelectField>
                <div className="text-[11px] text-slate-500">
                  Placeholders disponíveis: {[...FIXED_PLACEHOLDERS, "produto", "destinatario", ...customFields.filter((f) => f.active !== false).map((f) => f.key)].map((key) => `{${key}}`).join(", ")}
                </div>
                <div>
                  <Label className="flex items-center gap-1.5 text-xs text-slate-400">
                    Mover lead para (obrigatório)
                    <HelpTooltip text="Escolha primeiro o funil de destino -- a etapa só fica disponível depois disso, já filtrada pras etapas daquele funil." />
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                    <SelectField
                      testid="auto-reminder-pipeline"
                      value={form.action_pipeline_id}
                      onChange={(value) => set({ action_pipeline_id: value, action_column_id: "" })}
                    >
                      <Option value="">Escolha o funil destino</Option>
                      {pipelines.map((pipeline) => <Option key={pipeline.id} value={pipeline.id}>{pipeline.name}</Option>)}
                    </SelectField>
                    <SelectField
                      testid="auto-reminder-column"
                      value={form.action_column_id}
                      onChange={(value) => set({ action_column_id: value })}
                      disabled={!form.action_pipeline_id}
                    >
                      <Option value="">{form.action_pipeline_id ? "Escolha a etapa destino" : "Escolha o funil primeiro"}</Option>
                      {columnsForActionPipeline.map((column) => <Option key={column.id} value={column.id}>{column.name}</Option>)}
                    </SelectField>
                  </div>
                  {(!form.action_pipeline_id || !form.action_column_id) && (
                    <div className="text-[11px] text-amber-400 mt-1">
                      Selecione o funil e a etapa — sem isso a automação não pode ser salva.
                    </div>
                  )}
                </div>
              </>
            )}
          </Panel>

          <Panel title="Status">
            <Label className="text-sm text-slate-300 flex items-center gap-2">
              <Switch data-testid="auto-enabled" checked={form.enabled} onCheckedChange={(value) => set({ enabled: value })} />
              Ativa
            </Label>
          </Panel>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800">Cancelar</Button>
          <Button data-testid="auto-submit" onClick={save} disabled={busy || !form.name.trim() || missingReminderDestination} className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold">
            {busy ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Panel({ title, helpText, children }) {
  return (
    <div className="bg-slate-800/40 border border-slate-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#25D366] font-bold">
        {title}
        {helpText && <HelpTooltip text={helpText} />}
      </div>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, helpText }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 text-xs text-slate-400">
        {label}
        {helpText && <HelpTooltip text={helpText} />}
      </Label>
      <Input
        type="number"
        min="0"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="bg-slate-800 border-slate-700 mt-1"
      />
    </div>
  );
}
